/**
 * WebSocket сервис для ProfitOption
 * Протокол v1.0.1 - полная совместимость с существующим ws-server.py
 */

import { store } from "../store";
import { API_CONFIG } from "../config";
import {
  setConnection,
  setConnectionStatus,
  setError,
  clearError,
} from "../store/slices/websocketSlice";
import { api } from "shared/config/api/api";
import {
  setTradingPairs,
  updateMarketOpenStatus,
  setSelectedPair,
  setChartData,
  addChartDataPoint,
  setHistoryData,
  setOrderHistory,
  setError as setTradingError,
  updateSettings,
  closeOrder,
  updateBalance,
  addOrder,
  setSelectedTimeframe,
  updateCurrentOHLC, // используем для агрегации OHLC по тикам
  clearChartData,
  addFixedPoint, // финализация свечи при переходе периода
  setLoading,
  setExpirationLimits,
  setExpirationBounds,
} from "../store/slices/tradingSlice";
import {
  MarketOpenUpdateData,
  ServerBasePrice,
  ServerChartType,
} from "./types";
import type { ExpirationBounds, TradingPair } from "shared/model";
import { setDrawingInstances } from "features/drawing-tool/model";
import {
  setIndicatorsInstances,
  setFavoriteIndicators,
} from "features/indicators/model";
import { ReconnectScheduler, PingWatchdog } from "shared/api/websocket";
import { getExpiryTimestamp } from "shared/utils";
import { setStoredPair } from "features/select-pair/model/pairSessionStorage";
import { resolveInitialPair } from "features/select-pair/model/resolveInitialPair";
import {
  extractNonPairSettings,
  extractSavedPairSymbol,
  isSettingsSaveAck,
  shouldSendCurrencySelection,
} from "./websocketPairLogic";

// Функция для воспроизведения звука победы
const playWinSound = () => {
  try {
    // Проверяем настройки звука в store
    const state = store.getState();
    const soundEnabled = state.trading.settings.sound_enabled;

    if (!soundEnabled) {
      return;
    }

    const audio = new Audio("/sound-win.wav");
    audio.volume = 0.5; // Устанавливаем громкость 50%
    audio.play().catch((error) => {});
  } catch (error) {}
};

// Типы сообщений WebSocket (протокол v1.0.1)
interface WSMessage {
  t: number; // тип сообщения (0-24)
  d?: any; // данные сообщения
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private isAuthenticated: boolean = false;

  // Реконнект с экспоненциальным backoff (капается, но никогда не сдаётся насовсем —
  // раньше после 5 попыток по 5с соединение переставало восстанавливаться до перезагрузки страницы).
  private readonly reconnectScheduler = new ReconnectScheduler({
    baseDelayMs: 1000,
    maxDelayMs: 30000,
    onReconnect: () => this.connect(),
  });

  // Watchdog: сервер шлёт t=0 (ping) периодически и сам считает связь разорванной,
  // если не получил pong >10с (см. WEBSOCKET_PROTOCOL.md). Раньше клиент никак не
  // проверял живость соединения со своей стороны — если сокет "зависал" без close(),
  // приложение могло молча простаивать без пинга сколь угодно долго.
  private readonly pingWatchdog = new PingWatchdog({
    checkIntervalMs: 10000,
    timeoutMs: 60000,
    onTimeout: () => this.ws?.close(),
  });

  private messageQueue: WSMessage[] = [];
  private currentSymbol: string | null = null;
  // Однократная инициализация пары на WS-соединение.
  // global symbol из t=16 намеренно не применяем — источник истины: sessionStorage + Redux вкладки.
  private pairInitializedForSession = false;

  // Локальный кэш последних цен (без Redux) + подписки на тики
  private lastPrices: Map<string, { price: number; timestamp: number }> =
    new Map();
  private priceSubscribers: Map<
    string,
    Set<(u: { pair: string; price: number; timestamp: number }) => void>
  > = new Map();

  // Небольшой троттлинг обновлений OHLC, чтобы не спамить Redux
  private lastOHLCUpdateAt: Map<string, number> = new Map();

  private serverTimeOffset: number = 0; // milliseconds: clientNow - serverNow (positive when client ahead)
  private offsetEwmaAlpha: number = 0.2; // smoothing factor for offset updates
  private lastServerSyncAt: number | null = null;
  private lastServerTs: number | null = null; // <--- добавлено свойство для последнего серверного времени

  constructor() {
    // pagehide покрывает и обычный уход со страницы, и попадание в bfcache —
    // без этого сервер держит "мёртвый" сокет до истечения своего таймаута,
    // из-за чего повторная загрузка страницы могла идти заметно дольше.
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", () => this.disconnect());
    }
  }

  // Утилита: нормализует различные форматы timestamp в UTC milliseconds
  private parseTimestamp(value: any): number {
    // Возвращаем текущее время как запасной вариант
    if (value === undefined || value === null) return Date.now();

    // Если передали уже число
    if (typeof value === "number") {
      const s = value.toString();
      // Если это секунды (10 цифр) — конвертируем в миллисекунды
      if (s.length === 10) return value * 1000;
      return value; // предполагаем, что это миллисекунды
    }

    // Если передали строку, которая состоит только из цифр
    if (typeof value === "string") {
      const digitsOnly = /^\d+$/;
      if (digitsOnly.test(value)) {
        // Преобразуем в число и проверим длину
        const n = parseInt(value, 10);
        const len = value.length;
        if (len === 10) return n * 1000; // секунды -> мс
        return n; // уже миллисекунды
      }

      // Если это ISO строка без указания временной зоны (например "2023-05-01T12:00:00"),
      // Date.parse будет трактовать её как локальное время. Мы хотим трактовать такие строки как UTC.
      // Поэтому, если строка содержит 'T' (ISO-like) и не содержит 'Z' или смещения (+/-), добавим 'Z'.
      const isoLike = /^\d{4}-\d{2}-\d{2}T.*$/;
      const hasTZ = /[zZ]|[+\-]\d{2}:?\d{2}$/;
      if (isoLike.test(value) && !hasTZ.test(value)) {
        const parsed = Date.parse(value + "Z");
        if (!isNaN(parsed)) return parsed;
      }

      // Попробуем обычный парсинг Date.parse (поддерживает ISO с Z и смещениями)
      const parsed = Date.parse(value);
      if (!isNaN(parsed)) return parsed;

      // Неизвестный формат — возвращаем текущее время
      return Date.now();
    }

    // Во всех остальных случаях — возвращаем текущее время
    return Date.now();
  }

  // Подписка на тики по символу. Вернёт функцию отписки
  public subscribePrice(
    pair: string,
    cb: (u: { pair: string; price: number; timestamp: number }) => void,
  ): () => void {
    const key = pair.toUpperCase();
    if (!this.priceSubscribers.has(key)) {
      this.priceSubscribers.set(key, new Set());
    }
    const set = this.priceSubscribers.get(key)!;
    set.add(cb);

    // Немедленно отдаем последний тик если есть
    const last = this.lastPrices.get(key);
    if (last) {
      try {
        cb({ pair: key, price: last.price, timestamp: last.timestamp });
      } catch {}
    }

    return () => {
      const s = this.priceSubscribers.get(key);
      if (s) {
        s.delete(cb);
        if (s.size === 0) this.priceSubscribers.delete(key);
      }
    };
  }

  private emitPriceUpdate(update: {
    pair: string;
    price: number;
    timestamp: number;
  }) {
    const set = this.priceSubscribers.get(update.pair.toUpperCase());
    if (!set) return;
    set.forEach((cb) => {
      try {
        cb(update);
      } catch {}
    });
  }

  public connect() {
    const state = store.getState();
    const { user, token } = state.auth;

    if (!user || !token) {
      return;
    }

    // Закрываем предыдущий сокет, если он ещё жив — иначе при повторном
    // вызове connect() (например, гонка эффектов) можно осиротить старое
    // соединение, которое продолжит попытки реконнекта параллельно с новым.
    this.closeSocket();

    // Используем URL из конфигурации
    const wsUrl = API_CONFIG.WEBSOCKET_URL;

    try {
      this.ws = new WebSocket(wsUrl);
      store.dispatch(setConnection(this.ws));

      this.ws.onopen = () => {
        store.dispatch(setConnectionStatus("connected"));
        store.dispatch(clearError());
        this.reconnectScheduler.reset();

        // Отправляем данные аутентификации
        const token = store.getState().auth.token;
        if (!token) return;

        const authData = {
          token,
        };

        this.ws!.send(JSON.stringify(authData));
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          if (!message.hasOwnProperty("t")) {
            return;
          }

          this.processMessage(message);
        } catch (error) {}
      };

      this.ws.onerror = (error) => {
        store.dispatch(setError("WebSocket connection error"));
      };

      this.ws.onclose = () => {
        store.dispatch(setConnectionStatus("disconnected"));
        this.isAuthenticated = false;
        // Новому сокету нужен повторный t=9; guard в sendCurrencySelection сравнивает с этим полем.
        this.currentSymbol = null;
        // Иначе после logout/reconnect ensureSelectedPair навсегда no-op → пара не выбирается.
        this.pairInitializedForSession = false;
        this.pingWatchdog.stop();
        this.reconnectScheduler.scheduleNext();
      };
    } catch (error) {
      store.dispatch(setError("Failed to create WebSocket connection"));
    }
  }

  public disconnect() {
    this.reconnectScheduler.cancel();
    this.closeSocket();
    this.pingWatchdog.stop();
    this.isAuthenticated = false;
    // Сброс подписки: после reconnect TradingDashboard снова отправит t=9 для той же пары.
    this.currentSymbol = null;
    this.pairInitializedForSession = false;
    store.dispatch(setConnection(null));
    store.dispatch(setConnectionStatus("disconnected"));
  }

  public getConnectionStatus(): boolean {
    return this.ws?.readyState === WebSocket.OPEN && this.isAuthenticated;
  }

  // Отвязывает обработчики перед закрытием, чтобы close() не порождал ещё
  // один разрыв соединения (и, как следствие, лишний реконнект) — сброс
  // сокета здесь всегда намеренный, его инициирует сам вызывающий код.
  private closeSocket() {
    if (!this.ws) return;

    this.ws.onopen = null;
    this.ws.onmessage = null;
    this.ws.onerror = null;
    this.ws.onclose = null;
    if (
      this.ws.readyState === WebSocket.OPEN ||
      this.ws.readyState === WebSocket.CONNECTING
    ) {
      this.ws.close();
    }
    this.ws = null;
  }

  private processMessage(message: WSMessage) {
    //TODO: добавить типизацию для всех сообщений, чтобы TS проверял структуру данных
    const messageType = message.t;
    const data = message.d;

    switch (messageType) {
      case 0: // Ping от сервера
        this.pingWatchdog.notifyPing();

        // При получении ping измеряем смещение часов клиента относительно сервера.
        try {
          const serverTsRaw = data?.ts;
          const serverTs = this.parseTimestamp(serverTsRaw);
          const clientRecv = Date.now();
          if (serverTs && !isNaN(serverTs)) {
            const measuredOffset = clientRecv - serverTs; // положительное когда клиент впереди
            if (this.lastServerSyncAt == null) {
              this.serverTimeOffset = measuredOffset;
            } else {
              // Сглаживаем изменения, чтобы уменьшить влияние jitter
              this.serverTimeOffset = Math.round(
                this.serverTimeOffset * (1 - this.offsetEwmaAlpha) +
                  measuredOffset * this.offsetEwmaAlpha,
              );
            }
            this.lastServerSyncAt = clientRecv;
            this.lastServerTs = serverTs; // <--- сохраняем последнее серверное время
          }
        } catch (e) {
          // ignore
        }
        this.sendPong(data);
        break;

      case 3: // Welcome message - успешная аутентификация
        this.isAuthenticated = true;
        store.dispatch(setConnectionStatus("connected"));
        this.pingWatchdog.start();

        // Отправляем накопленные сообщения
        this.flushMessageQueue();

        // Запрашиваем список валютных пар (как в первой версии)
        this.requestCurrencyPairs();

        // Запрашиваем историю ордеров сразу после аутентификации
        setTimeout(() => {
          this.requestOrderHistory();
        }, 3000);
        break;

      case 4: // Ошибка аутентификации
        store.dispatch(setError("Authentication failed"));
        break;

      case 8: // Список валютных пар
        this.handleCurrencyPairs(data);

        // После получения списка валютных пар запрашиваем настройки пользователя
        this.requestUserSettings();
        break;

      case 10: // Подтверждение выбора валютной пары
        // Игнорируем устаревшие подтверждения: при быстром переключении пар
        // ответы t=10 могут прийти не в том порядке, в котором ушли t=9,
        // и без этой проверки поздний ответ для уже покинутой пары
        // откатывал бы selectedPair (и, как следствие, Payout) назад.
        if (
          data &&
          data.pair &&
          data.pair.toUpperCase() === this.currentSymbol?.toUpperCase()
        ) {
          store.dispatch(setSelectedPair(data.pair));
          if (data.expiration_limits) {
            store.dispatch(setExpirationLimits(data.expiration_limits));
          }
        }
        break;

      case 12: // Исторические данные
        this.handleHistoricalData(data);
        break;

      case 14: // Обновление цены в реальном времени
        this.handlePriceUpdate(data);
        break;

      case 16: // Ответ на запрос настроек пользователя
        this.handleUserSettings(data);
        break;

      case 17: // Обновление баланса
        if (data) {
          const currentState = store.getState();
          const { accountPreferenceSaved, real_balance } =
            currentState.trading.settings;
          const prevReal = currentState.trading.balance.real;
          const newReal = data.real_balance || 0;

          store.dispatch(
            updateBalance({
              demo: data.demo_balance || 0,
              real: newReal,
            }),
          );

          // При первом получении баланса (prevReal === 0) и без сохранённого предпочтения:
          // если реальный баланс > 0 — переключаем на real
          if (
            !accountPreferenceSaved &&
            prevReal === 0 &&
            newReal > 0 &&
            !real_balance
          ) {
            store.dispatch(
              updateSettings({
                demo_balance: false,
                real_balance: true,
                accountPreferenceSaved: true,
              }),
            );
          }
        }
        break;

      case 20: // Ответ на открытие ордера
        this.handleOrderResponse(data);
        break;

      case 22: // Закрытие ордера
        this.handleOrderClosed(data);
        break;

      case 24: // История ордеров
        this.handleOrderHistory(data);
        break;

      case 26: // Ответ на запрос пополнения баланса
        this.handleDepositResponse(data);
        break;

      case 27: // Дельта is_market_open по символам
        this.handleMarketOpenUpdate(data);
        break;

      default:
        break;
    }
  }

  // Обработка списка валютных пар
  private handleCurrencyPairs(data: any) {
    if (!data || !data.meta) return;

    const tradingPairs: TradingPair[] = Object.entries(data.meta).map(
      ([symbol, meta]: [string, any]) => ({
        symbol,
        id: meta.id,
        assetType: meta.asset_type,
        icon: meta.icon_url,
        winPayoutRatio: meta.win_payout_ratio,
        winPayoutPercent:
          typeof meta.win_payout_percent === "number"
            ? meta.win_payout_percent
            : meta.win_payout_ratio * 100,
        isMarketOpen: meta.is_market_open, // добавляем флаг открытости рынка
      }),
    );

    store.dispatch(setTradingPairs(tradingPairs));
    this.syncExpirationBounds(data.meta);
    // t=8: только sessionStorage; global symbol из t=16 — на cold start один раз.
    this.ensureSelectedPair(tradingPairs, { sessionStorageOnly: true });
  }

  // t=8 несёт только min/max (пресетов нет) — обновляем их для текущей пары.
  private syncExpirationBounds(meta: Record<string, ExpirationBounds>) {
    const { selectedPair } = store.getState().trading;
    const bounds = selectedPair ? meta[selectedPair] : undefined;
    if (bounds) store.dispatch(setExpirationBounds(bounds));
  }

  // GET settings (t=16): demo/real, sound, indicators, drawing; symbol — один раз на cold start.
  // Повторные t=16 не перезаписывают пару (multi-tab ping-pong fix).
  private handleUserSettings(settings: Record<string, unknown> | null) {
    if (isSettingsSaveAck(settings)) return;

    if (settings) {
      const {
        settingsPatch,
        drawingInstances,
        indicatorInstances,
        indicatorFavorites,
      } = extractNonPairSettings(settings, store.getState().trading.settings);

      if (settingsPatch) {
        store.dispatch(updateSettings(settingsPatch));
      }

      if (drawingInstances) {
        store.dispatch(setDrawingInstances(drawingInstances));
      }

      if (indicatorInstances) {
        store.dispatch(setIndicatorsInstances(indicatorInstances));
      }

      if (indicatorFavorites) {
        store.dispatch(setFavoriteIndicators(indicatorFavorites));
      }
    }

    this.ensureSelectedPair(undefined, {
      savedSymbol: extractSavedPairSymbol(settings),
    });

    setTimeout(() => {
      this.requestOrderHistory();
    }, 2000);
  }

  /**
   * Cold start: sessionStorage → savedSymbol (t=16) → market-open → t=9.
   * Reconnect: если пара уже в Redux, но currentSymbol сброшен — повторный t=9.
   */
  private ensureSelectedPair(
    pairsOverride?: TradingPair[],
    options?: { savedSymbol?: string | null; sessionStorageOnly?: boolean },
  ) {
    const { selectedPair, currencyPairs } = store.getState().trading;
    const pairs = pairsOverride ?? currencyPairs;
    if (pairs.length === 0) {
      return;
    }

    if (selectedPair) {
      if (this.currentSymbol !== selectedPair) {
        this.sendCurrencySelection(selectedPair);
      }
      this.pairInitializedForSession = true;
      return;
    }

    if (this.pairInitializedForSession) {
      return;
    }

    const userId = store.getState().auth.user?.id;
    if (userId == null) {
      return;
    }

    const symbol = resolveInitialPair({
      userId,
      currencyPairs: pairs,
      selectedPair,
      savedSymbol: options?.savedSymbol,
      sessionStorageOnly: options?.sessionStorageOnly,
    });

    if (!symbol) {
      return;
    }

    setStoredPair(userId, symbol);
    store.dispatch(setSelectedPair(symbol));
    this.sendCurrencySelection(symbol);
    this.pairInitializedForSession = true;
  }

  // Обработка исторических данных
  private handleHistoricalData(data: any) {
    if (!data || !data.pair || !Array.isArray(data.prices)) {
      // Скрываем spinner при получении некорректного ответа
      store.dispatch(setLoading({ key: "history", value: false }));
      return;
    }

    // Если в сообщении нет period, игнорируем его и не перерисовываем график
    if (!data.period) {
      // Скрываем spinner — сервер вернул пакет без period
      store.dispatch(setLoading({ key: "history", value: false }));
      return;
    }

    const pair = data.pair;
    const period = data.period;
    const chartType = data.chart_type || "line";

    // Преобразуем исторические данные в формат для графика в зависимости от типа
    let chartData;

    if (chartType === "candles" || chartType === "bars") {
      // Для свечей и баров используем OHLC данные от сервера
      chartData = data.prices.map((priceData: any) => {
        // Нормализуем timestamp в миллисекунды UTC
        const timestamp = this.parseTimestamp(priceData.timestamp);

        // Возвращаем OHLC данные как есть от сервера (timestamp в мс)
        return {
          timestamp: timestamp,
          open: parseFloat(priceData.open),
          high: parseFloat(priceData.high),
          low: parseFloat(priceData.low),
          close: parseFloat(priceData.close),
          chart_type: chartType, // Добавляем информацию о типе графика
        };
      });
    } else {
      // Для линейного графика используем данные о цене
      chartData = data.prices.map((priceData: any) => {
        const timestamp = this.parseTimestamp(priceData.timestamp);

        return {
          timestamp: timestamp,
          price: parseFloat(priceData.price || priceData),
          volume: priceData.volume || 0,
          chart_type: chartType,
        };
      });
    }

    // Сохраняем данные для графика с информацией о типе
    store.dispatch(
      setChartData({
        symbol: pair,
        data: chartData,
        chartType: chartType,
      }),
    );

    // Также сохраняем в historyData для совместимости (оставляем оригинальные данные от сервера)
    store.dispatch(setHistoryData({ symbol: pair, data: data.prices }));

    // Отключаем флаг загрузки истории — данные получены и можно отрисовать график
    store.dispatch(setLoading({ key: "history", value: false }));
  }

  // Вспомогательный метод для получения выбранного таймфрейма
  private getSelectedTimeframe(): string {
    const state = store.getState();
    return state.trading.selectedTimeframe || "1M";
  }

  // Конвертация таймфрейма в миллисекунды
  private getPeriodMs(tf: string): number {
    switch (tf) {
      case "10S":
        return 10_000;
      case "30S":
        return 30_000;
      case "1M":
        return 60_000;
      case "5M":
        return 300_000;
      case "15M":
        return 900_000;
      case "1H":
        return 3_600_000;
      case "4H":
        return 14_400_000;
      case "1D":
        return 86_400_000;
      default:
        return 60_000;
    }
  }

  // Обработка обновления цены (тик)
  private handlePriceUpdate(data: any) {
    if (!data || !data.pair || data.price === undefined) {
      return;
    }

    if (this.currentSymbol !== data.pair) {
      return;
    }

    // t=14.d.timestamp — publisher epoch ms (Redis ts); do not apply serverToClientTs offset
    let timestamp: number;
    if (data.timestamp) {
      timestamp = this.parseTimestamp(data.timestamp);
    } else {
      timestamp = Date.now();
    }
    if (isNaN(timestamp) || timestamp <= 0) timestamp = Date.now();

    const price = parseFloat(data.price);

    // 1) Обновляем локальный кэш и уведомляем подписчиков (мгновенно, без Redux)
    const pairKey = data.pair.toUpperCase(); // ключ для подписчиков
    this.lastPrices.set(pairKey, { price, timestamp });
    this.emitPriceUpdate({ pair: pairKey, price, timestamp });

    // 2) Агрегируем тики в OHLC для текущего таймфрейма, но с лёгким троттлингом
    try {
      const timeframe = this.getSelectedTimeframe();
      const periodMs = this.getPeriodMs(timeframe);
      const periodEndTime =
        Math.floor(timestamp / periodMs) * periodMs + periodMs; // конец текущего периода
      const periodStartTime = periodEndTime - periodMs; // НАЧАЛО периода

      // ВАЖНО: ключи в Redux должны совпадать с ключами, используемыми компонентами (без toUpperCase)
      const symbolKey = this.currentSymbol || data.pair;

      const state = store.getState();
      const currentPoint = state.trading.currentPeriodPoints[symbolKey];
      const periodChanged =
        !!currentPoint && currentPoint.endTime !== periodEndTime;

      // Если начался новый период, фиксируем предыдущую свечу в chartFixedPoints
      if (periodChanged) {
        const prev = currentPoint;
        const closePrice = prev.close ?? prev.price ?? price;
        store.dispatch(
          addFixedPoint({
            symbol: symbolKey,
            point: {
              // Фиксируем по ВРЕМЕНИ НАЧАЛА периода (совпадает с историческими данными)
              timestamp: periodStartTime,
              price: closePrice,
              open: prev.open ?? closePrice,
              high: prev.high ?? closePrice,
              low: prev.low ?? closePrice,
              close: closePrice,
            },
          } as any),
        );
      }

      // Троттлим обновления текущей свечи в Redux (например, раз в 150мс) или всегда при смене периода
      const lastAt = this.lastOHLCUpdateAt.get(symbolKey) || 0;
      const shouldUpdate =
        periodChanged || timestamp - lastAt > 150 || !currentPoint;
      if (shouldUpdate) {
        store.dispatch(
          updateCurrentOHLC({ symbol: symbolKey, price, periodEndTime } as any),
        );
        this.lastOHLCUpdateAt.set(symbolKey, timestamp);
      }
    } catch (e) {}

    // Если понадобится периодическая агрегация OHLC — можно реализовать батчинг по таймеру
  }

  // Обработка ответа на ордер
  private handleOrderResponse(data: any) {
    if (!data) return;

    const state = store.getState();
    const currentPrice =
      state.trading.prices[data.symbol || this.currentSymbol || ""]?.price;

    // Нормализуем timestamp от сервера и корректируем по offset
    const serverTs =
      this.serverToClientTs(data.timestamp) ??
      this.parseTimestamp(data.timestamp);
    const serverCloseTs =
      this.serverToClientTs(data.close_timestamp) ??
      (data.close_timestamp
        ? this.parseTimestamp(data.close_timestamp)
        : undefined);

    if (data.status === "success") {
      setTimeout(() => {
        this.requestOrderHistory();
      }, 1000);
    } else if (data.status === "error") {
      store.dispatch(setTradingError(data.message || "Order failed"));
    }
  }

  // Обработка закрытия ордера
  private handleOrderClosed(data: any) {
    if (!data || !data.uuid) return;

    // closeTimestamp хранится в той же шкале, что и getCurrentServerTime()
    // (истинное серверное время) — serverToClientTs() тут не подходит, она
    // намеренно возвращает время в "испорченную" клиентскую шкалу и вместе
    // с getCurrentServerTime() удваивала бы компенсацию рассинхронизации часов.
    const closeTs = data.close_timestamp
      ? this.parseTimestamp(data.close_timestamp)
      : this.getCurrentServerTime();
    const closePrice = data.close_price || data.closePrice || 0;
    const profit = data.profit || 0;

    // Обновляем ордер в store, передавая closeTimestamp чтобы order в истории имел корректное время
    store.dispatch(
      closeOrder({
        id: data.uuid,
        closePrice: closePrice,
        profit: profit,
        closeTimestamp: closeTs,
      }),
    );

    store.dispatch(api.util.invalidateTags(["UserStats", "UserBalanceStatus"]));

    // Только история ордеров. Payout/meta обновляется server push t=8 (payout-live-sync).
    // Раньше здесь был requestCurrencyPairs → t=7 → GET settings → t=9 и reload loop между вкладками.
    setTimeout(() => {
      this.requestOrderHistory();
    }, 1000);

    // Воспроизводим звук победы, если прибыль >= 0 (победа или ничья)
    if (profit >= 0) {
      if (profit > 0) {
      } else {
      }
      playWinSound();
    } else {
    }
  }

  // Обработка истории ордеров
  private handleOrderHistory(data: any) {
    if (!data || !Array.isArray(data.orders)) return;

    const { currencyPairs } = store.getState().trading;

    // Преобразуем ордера в нужный формат
    const orders = data.orders.map((order: any) => {
      const openTs = this.parseTimestamp(order.open_time || order.timestamp);
      const closeTsVal = order.close_time || order.closeTimestamp || null;
      // Та же шкала, что у openTs и getCurrentServerTime() — см. handleOrderClosed.
      const closeTs = closeTsVal ? this.parseTimestamp(closeTsVal) : null;

      return {
        id: order.uuid || order.id || Date.now().toString(),
        symbol: order.symbol,
        type: order.type,
        amount: order.amount,
        openPrice: order.open_price || order.openPrice,
        closePrice: order.close_price || order.closePrice,
        timestamp: openTs,
        closeTimestamp: closeTs,
        status:
          order.status === "open5" || order.status === "open"
            ? "open"
            : "closed",
        profit: order.profit,
        // ИСПРАВЛЕНИЕ: Правильно маппим balance_type из протокола WebSocket v1.0.1
        demo_balance: order.balance_type === "demo",
        real_balance: order.balance_type === "real",
        // Добавляем новые поля для потенциальной прибыли
        potential_profit_win:
          order.potential_profit_win ||
          Number(
            (
              (currencyPairs.find((p) => p.symbol === order.symbol)
                ?.winPayoutRatio ?? 0) * order.amount
            ).toFixed(2),
          ),
        potential_profit_loss: order.potential_profit_loss || -order.amount,
      };
    });

    // Разделяем на активные и закрытые ордера
    const activeOrders = orders.filter((order: any) => order.status === "open");
    const closedOrders = orders.filter(
      (order: any) => order.status === "closed",
    );

    // Очищаем текущие активные ордера и добавляем новые
    // Сначала очищаем store от старых активных ордеров
    const currentState = store.getState();
    const currentActiveOrders = currentState.trading.orders;

    // Удаляем все старые активные ордера
    currentActiveOrders.forEach((order) => {
      store.dispatch(
        closeOrder({
          id: order.id,
          closePrice: 0,
          profit: 0,
        }),
      );
    });

    // Добавляем новые активные ордера
    activeOrders.forEach((order: any) => {
      store.dispatch(addOrder(order));
    });

    // Обновляем историю закрытых ордеров
    store.dispatch(setOrderHistory(closedOrders));
  }

  // Запрос списка валютных пар (тип 7)
  private requestCurrencyPairs() {
    this.sendMessage({
      t: 7,
      d: { action: "get_currency_pairs" },
    });
  }

  // Запрос настроек пользователя (тип 15)
  private requestUserSettings() {
    this.sendMessage({
      t: 15,
      d: { action: "get_settings" },
    });
  }

  // Сохранение произвольного патча в настройках пользователя (тип 15)
  public saveUserSettings(patch: Record<string, unknown>): void {
    this.sendMessage({
      t: 15,
      d: { action: "save_settings", ...patch },
    });
  }

  // Отправка выбора валютной пары (тип 9)
  public sendCurrencySelection(currencyPair: string) {
    // Не дублируем t=9, если подписка на этом соединении уже на той же паре.
    if (!shouldSendCurrencySelection(this.currentSymbol, currencyPair)) {
      return;
    }

    // Если символ действительно изменился, очищаем данные графика
    if (this.currentSymbol !== currencyPair) {
      // Очищаем все данные графика (они будут перезапрошены для нового символа)
      store.dispatch(clearChartData({}));

      // Обновляем текущий символ
      this.currentSymbol = currencyPair;
    }

    this.sendMessage({
      t: 9,
      d: { pair: currencyPair.toUpperCase() },
    });
  }

  private sendMessage(message: WSMessage) {
    if (
      this.ws &&
      this.ws.readyState === WebSocket.OPEN &&
      this.isAuthenticated
    ) {
      this.ws.send(JSON.stringify(message));
    } else {
      // Добавляем в очередь если соединение не готово
      this.messageQueue.push(message);
    }
  }

  private flushMessageQueue() {
    if (this.messageQueue.length > 0) {
      this.messageQueue.forEach((message) => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify(message));
        }
      });
      this.messageQueue = [];
    }
  }

  private sendPong(data?: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const pongMessage: WSMessage = {
        t: 1, // Тип 1 = pong
        d: { ts: data?.ts }, // Возвращаем тот же timestamp
      };
      this.ws.send(JSON.stringify(pongMessage));
    }
  }

  // Публичные методы для взаимодействия с WebSocket

  public placeOrder(
    pair: string,
    direction: "call" | "put",
    amount: number,
    duration: number,
  ) {
    if (this.lastServerSyncAt == null) {
      alert(
        "Ждём синхронизации времени с сервером, попробуйте через пару секунд.",
      );
      return;
    }
    const state = store.getState();
    const { settings, prices, switchTimeEnabled } = state.trading;

    if (!this.isAuthenticated) {
      alert("⚠️ Соединение с сервером потеряно. Перезагрузите страницу.");
      return;
    }

    const orderType = direction === "call" ? "buy" : "sell";

    // ИСПРАВЛЕНИЕ: Определяем с какого счета списывать средства на основе настроек
    // Приоритет: если активен реальный счет - используем его, иначе демо
    let isDemoAccount = true; // По умолчанию демо

    if (settings.real_balance && !settings.demo_balance) {
      // Только реальный счет активен
      isDemoAccount = false;
    } else if (settings.demo_balance && !settings.real_balance) {
      // Только демо счет активен
      isDemoAccount = true;
    } else if (settings.real_balance && settings.demo_balance) {
      // Оба счета активны - используем реальный (приоритет)
      isDemoAccount = false;
    } else {
      // Ни один счет не активен - используем демо по умолчанию
      isDemoAccount = true;
    }

    // Новый способ: вычисляем "текущее" серверное время
    // lastServerTs = последнее полученное серверное время (из ping)
    // lastServerSyncAt = когда оно было получено (client time)
    // Если нет lastServerTs, используем старый способ
    let serverNow: number;
    if (
      this.lastServerSyncAt != null &&
      typeof this.lastServerTs === "number"
    ) {
      serverNow = this.lastServerTs + (Date.now() - this.lastServerSyncAt);
    } else {
      serverNow = Date.now() - (this.serverTimeOffset || 0);
    }
    const currentTime = serverNow;
    // В режиме Switch Time сделка должна закрыться в момент, отмеченный
    // флажком на графике (округление до минуты), а не через duration секунд.
    const closeTime = getExpiryTimestamp(
      currentTime,
      duration,
      switchTimeEnabled,
    );

    // Вычисляем конец текущего периода для выбранного таймфрейма —
    // используем его как timestamp для метки, чтобы она привязывалась
    // к той же позиции, что и формирующаяся свеча/бар в графике.
    const timeframe = this.getSelectedTimeframe();
    const periodMs = this.getPeriodMs(timeframe);
    const periodEndTime =
      Math.floor(currentTime / periodMs) * periodMs + periodMs;
    const markerTimestamp = periodEndTime;

    // Генерируем временный id для маркера до ответа сервера (заменим на order_id при t=20)
    const tempId = `${pair}_${currentTime}_${Math.random().toString(36).slice(2, 8)}`;

    const orderMessage: WSMessage = {
      t: 19,
      d: {
        demo_balance: isDemoAccount,
        real_balance: !isDemoAccount,
        timestamp: currentTime,
        close_timestamp: closeTime,
        type: orderType,
        amount: amount,
        symbol: pair,
        switch_time: switchTimeEnabled,
        client_temp_id: tempId,
      },
    };

    // Берем цену из локального кэша, а при отсутствии — из Redux (fallback)
    let tempPrice = this.lastPrices.get(pair)?.price;
    if (tempPrice === undefined) {
      tempPrice = prices[pair]?.price;
    }

    if (tempPrice === undefined) {
      alert("⚠️ Не удалось получить текущую цену. Попробуйте позже.");
      return;
    }

    // Используем вычисленный markerTimestamp (end of current period) чтобы метка
    // корректно отображалась на актуальной свече/баре, а не слевее.

    // Отправка сообщения
    this.sendMessage(orderMessage);
  }

  public switchAccount(accountType: "demo" | "real") {
    if (!this.isAuthenticated) {
      return;
    }

    // Здесь можно добавить логику переключения счета если нужно
  }

  public requestBalance() {
    if (!this.isAuthenticated) {
      return;
    }

    // Запрос обновления баланса можно реализовать если нужно
  }

  public requestOrderHistory() {
    // Позволяем вызывать до аутентификации: сообщение уйдет из очереди после welcome (t=3)
    this.sendMessage({
      t: 23, // Тип сообщения get_order_history
      d: {
        limit: 50, // Запрашиваем больше ордеров для лучшего отображения
      },
    });
  }

  public selectCurrencyPair(pair: string) {
    // Отправляем выбор валютной пары (тип 9)
    this.sendCurrencySelection(pair);
  }

  /**
   * Запрос исторических данных с указанным периодом
   * @param pair Валютная пара
   * @param period Период (1M, 5M, 15M, 1H, 4H, 1D)
   * @param chartType Тип графика (line, candles, bars) - необязательный
   */
  public requestHistoricalData(
    pair: string,
    period: string,
    chartType?: string,
  ) {
    // Разрешаем вызывать до аутентификации: запрос будет поставлен в очередь и отправлен после welcome (t=3)
    // Конвертируем период в формат сервера
    const periodMap: Record<string, string> = {
      "10S": "10s",
      "30S": "30s",
      "1M": "1m",
      "5M": "5m",
      "15M": "15m",
      "1H": "1h",
      "4H": "4h",
      "1D": "1d",
    };

    const serverPeriod = periodMap[period] || "1m";

    // Формируем данные запроса
    const requestData: any = {
      pair: pair,
      period: serverPeriod,
    };

    // Добавляем chart_type если он указано
    if (chartType) {
      requestData.chart_type = chartType;
    } else {
    }

    this.sendMessage({
      t: 11, // Тип сообщения get_history
      d: requestData,
    });

    // Помечаем стор как загружающий историю для отображения spinner'а
    store.dispatch(setLoading({ key: "history", value: true }));

    // Сохраняем в настройках выбранный таймфрейм
    store.dispatch(
      updateSettings({
        timeframe: period,
      }),
    );

    // Обновляем выбранный таймфрейм в сторе
    store.dispatch(setSelectedTimeframe(period));
  }
  /**
   * Запрос исторических данных с возвратом Promise (напрямую, без Redux)
   * @param pair Валютная пара
   * @param period Период (1M, 5M, 15M, 1H, 4H, 1D)
   * @param chartType Тип графика (line, candles, bars) - необязательный
   * @returns Promise с историческими данными
   */
  public fetchHistoricalData(
    pair: string,
    period: string,
    chartType?: string,
  ): Promise<{
    pair: string;
    period: string;
    chart_type?: ServerChartType;
    prices: ServerBasePrice[];
  } | null> {
    return new Promise((resolve, reject) => {
      // Конвертируем период в формат сервера
      const periodMap: Record<string, string> = {
        "10S": "10s",
        "30S": "30s",
        "1M": "1m",
        "5M": "5m",
        "15M": "15m",
        "1H": "1h",
        "4H": "4h",
        "1D": "1d",
      };

      const serverPeriod = periodMap[period] || "1m";

      // Уникальный ID для этого запроса
      const requestId = `${pair}_${serverPeriod}_${Date.now()}`;

      // Временный обработчик сообщений
      const messageHandler = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data);

          // Проверяем, что это ответ на наш запрос (t=12 - исторические данные)
          if (
            message.t === 12 &&
            message.d?.pair === pair &&
            message.d?.period === serverPeriod
          ) {
            // Убираем обработчик
            this.ws?.removeEventListener("message", messageHandler);
            clearTimeout(timeoutId);

            // Возвращаем данные
            resolve(message.d);
          }
        } catch (e) {
          // Игнорируем ошибки парсинга
        }
      };

      // Добавляем временный обработчик
      this.ws?.addEventListener("message", messageHandler);

      // Таймаут на случай, если сервер не ответит
      const timeoutId = setTimeout(() => {
        this.ws?.removeEventListener("message", messageHandler);
        reject(new Error(`Historical data request timeout for ${pair}`));
      }, 10000);

      // Формируем данные запроса
      const requestData: {
        pair: string;
        period: string;
        chart_type?: string;
      } = {
        pair: pair,
        period: serverPeriod,
      };

      if (chartType) {
        requestData.chart_type = chartType;
      }

      // Отправляем запрос
      this.sendMessage({
        t: 11, // Тип сообщения get_history
        d: requestData,
      });
    });
  }

  /**
   * Отправка запроса на пополнение баланса (тип 25)
   * @param depositData Данные для пополнения баланса
   */
  public sendDepositRequest(depositData: {
    balance_type: "demo" | "real";
    amount: number;
    timestamp: number;
  }) {
    if (!this.isAuthenticated) {
      return;
    }

    this.sendMessage({
      t: 25, // Тип сообщения deposit_request
      d: depositData,
    });
  }

  // Обработка ответа на запрос пополнения баланса
  private handleDepositResponse(data: any) {
    if (!data) {
      return;
    }

    if (data.status === "success") {
      // Показываем уведомление об успехе
      alert(
        `✅ Balance successfully deposited!\n\nAccount: ${data.balance_type.charAt(0).toUpperCase() + data.balance_type.slice(1)}\nAmount: $${data.amount.toLocaleString()}\nNew Balance: $${data.new_balance.toLocaleString()}`,
      );

      // Обновляем баланс в store (сервер также должен отправить сообщение типа 17)
      const currentState = store.getState();
      const currentBalance = currentState.trading.balance;

      if (data.balance_type === "demo") {
        store.dispatch(
          updateBalance({
            demo: data.new_balance,
            real: currentBalance.real,
          }),
        );
      } else if (data.balance_type === "real") {
        store.dispatch(
          updateBalance({
            demo: currentBalance.demo,
            real: data.new_balance,
          }),
        );
      }
    } else if (data.status === "error") {
      // Показываем уведомление об ошибке
      alert(`❌ Deposit failed!\n\nReason: ${data.message}`);

      // Можно также добавить в store информацию об ошибке
      store.dispatch(setTradingError(`Deposit failed: ${data.message}`));
    } else {
    }
  }

  private handleMarketOpenUpdate(data: unknown): void {
    if (
      typeof data !== "object" ||
      data === null ||
      !("symbols" in data) ||
      typeof (data as { symbols: unknown }).symbols !== "object" ||
      (data as { symbols: unknown }).symbols === null
    )
      return;

    const { symbols } = data as MarketOpenUpdateData;
    store.dispatch(updateMarketOpenStatus(symbols));
  }

  // Нормализует timestamp, возвращает null если серверный timestamp не передан
  public serverToClientTs(value: any): number | null {
    if (value === undefined || value === null) return null;

    try {
      const ts = this.parseTimestamp(value);
      if (!ts || isNaN(ts)) return null;
      // Приводим серверный timestamp к клиентской временной базе (компенсация сдвига часов)
      const adjusted = ts + (this.serverTimeOffset || 0);
      return adjusted;
    } catch (e) {
      return null;
    }
  }

  public getLastPublisherTsMs(symbol?: string): number | null {
    const key = (symbol ?? this.currentSymbol)?.toUpperCase();
    if (!key) return null;
    return this.lastPrices.get(key)?.timestamp ?? null;
  }

  // Возвращает "текущее" серверное время
  public getCurrentServerTime(): number {
    if (
      this.lastServerSyncAt != null &&
      typeof this.lastServerTs === "number"
    ) {
      return this.lastServerTs + (Date.now() - this.lastServerSyncAt);
    }
    return Date.now() - (this.serverTimeOffset || 0);
  }
}

// Экспортируем единственный экземпляр сервиса
const websocketService = new WebSocketService();
export default websocketService;
